import { jsPDF } from "jspdf";
import LogoEckenmoorhexen from "../../../assets/images/LogoEckenmoorhexen.png";

export interface InvoiceEntry {
  productName: string;
  articleNumbersSummary?: string;
  price: number;
  quantity: number;
  selectedOptionsSummary?: string;
}

export interface InvoiceCustomer {
  title?: string;
  name: string;
  email?: string;
  kleidlesnummer?: string;
}

export interface InvoiceDocumentData {
  title: string;
  subtitle?: string;
  invoiceNumber: string;
  dateLabel: string;
  referenceLabel?: string;
  customer?: InvoiceCustomer;
  paymentIban?: string;
  entries: InvoiceEntry[];
  footerHint?: string;
  fileName?: string;
}

const CLUB_NAME = "Eckenmoorhexen Sulgen e.V.";
const CLUB_ADDRESS = ["Rottweiler Strasse 5", "78713 Schramberg"];
const CLUB_EMAIL = "info@eckenmoorhexen.de";
const BRAND = {
  accent: [181, 115, 76] as const,
  accentDark: [181, 115, 76] as const,
  accentSoft: [224, 190, 164] as const,
  surface: [246, 236, 226] as const,
  textBlack: [0, 0, 0] as const,
  text: [255, 255, 255] as const,
  muted: [143, 143, 143] as const,
  line: [215, 206, 193] as const,
};

const formatPrice = (value: number): string =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value);

const ensurePdfFileName = (fileName?: string) => {
  if (!fileName) {
    return "rechnung.pdf";
  }

  return fileName.toLowerCase().endsWith(".pdf") ? fileName : `${fileName}.pdf`;
};

const formatIban = (value: string) =>
  value
    .replace(/\s+/g, "")
    .toUpperCase()
    .replace(/(.{4})/g, "$1 ")
    .trim();

const loadLogoDataUrl = async (): Promise<string | null> => {
  try {
    const response = await fetch(LogoEckenmoorhexen);
    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(typeof reader.result === "string" ? reader.result : null);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const drawTextBlock = (
  doc: jsPDF,
  lines: string[],
  x: number,
  startY: number,
  options?: { maxWidth?: number; fontSize?: number; color?: readonly [number, number, number] }
) => {
  const fontSize = options?.fontSize ?? 10;
  const color = options?.color ?? BRAND.textBlack;
  const maxWidth = options?.maxWidth ?? 70;
  let y = startY;

  doc.setFontSize(fontSize);
  doc.setTextColor(...color);

  lines.forEach((line) => {
    const wrapped = doc.splitTextToSize(line, maxWidth);
    doc.text(wrapped, x, y);
    y += wrapped.length * (fontSize * 0.45 + 1.2);
  });

  return y;
};

export const downloadInvoiceDocument = async (data: InvoiceDocumentData): Promise<boolean> => {
  if (data.entries.length === 0) {
    return false;
  }

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;
  const columnX = [margin, 92, 132, 152, 177];
  const columnWidths = [72, 38, 18, 25, 17];
  const lineHeight = 5;

  const addHeader = async () => {
    doc.setFillColor(...BRAND.accentDark);
    doc.roundedRect(margin, margin, contentWidth, 30, 4, 4, "F");
    doc.setDrawColor(...BRAND.line);
    doc.roundedRect(margin, margin, contentWidth, 30, 4, 4, "S");

    const logoDataUrl = await loadLogoDataUrl();
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, "PNG", margin + 4, margin + 4, 22, 22, undefined, "FAST");
    }

    doc.setTextColor(...BRAND.text);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(data.title, margin + 30, margin + 10);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.text);
    doc.text(data.subtitle || "Rechnung auf Basis der erfassten Reservierungsdaten.", margin + 30, margin + 17);

    const metaX = pageWidth - margin - 52;
    doc.setFillColor(255, 255, 255);
    doc.roundedRect(metaX, margin + 4, 48, 22, 3, 3, "F");
    doc.setDrawColor(...BRAND.line);
    doc.roundedRect(metaX, margin + 4, 48, 22, 3, 3, "S");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(...BRAND.muted);
    doc.text("RECHNUNGSNUMMER", metaX + 3, margin + 9);
    doc.text("DATUM", metaX + 3, margin + 17);

    doc.setFontSize(9);
    doc.setTextColor(...BRAND.textBlack);
    doc.text(data.invoiceNumber, metaX + 3, margin + 13);
    doc.text(data.dateLabel, metaX + 3, margin + 21);
  };

  await addHeader();

  let y = margin + 38;

  doc.setFillColor(...BRAND.surface);
  doc.roundedRect(margin, y, 84, 38, 3, 3, "F");
  doc.roundedRect(pageWidth - margin - 84, y, 84, 38, 3, 3, "F");
  doc.setDrawColor(...BRAND.line);
  doc.roundedRect(margin, y, 84, 38, 3, 3, "S");
  doc.roundedRect(pageWidth - margin - 84, y, 84, 38, 3, 3, "S");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...BRAND.accent);
  doc.text("RECHNUNGSSTELLER", margin + 4, y + 6);
  doc.text("EMPFÄNGER", pageWidth - margin - 80, y + 6);

  drawTextBlock(doc, [CLUB_NAME, ...CLUB_ADDRESS, CLUB_EMAIL], margin + 4, y + 11, { maxWidth: 74, fontSize: 10 });

  const customerLines = data.customer
    ? [data.customer.title, data.customer.name, data.customer.email, data.customer.kleidlesnummer ? `Kleidlesnummer: ${data.customer.kleidlesnummer}` : undefined].filter(
        (line): line is string => Boolean(line)
      )
    : ["Keine Empfängerdaten hinterlegt"];

  drawTextBlock(doc, customerLines, pageWidth - margin - 80, y + 11, { maxWidth: 74, fontSize: 10 });

  y += 46;

  if (data.referenceLabel) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.textBlack);
    doc.text("REFERENZ", margin, y);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...BRAND.textBlack);
    doc.text(data.referenceLabel, margin + 22, y);
    y += 8;
  }

  const ensureSpace = (requiredHeight: number) => {
    if (y + requiredHeight <= pageHeight - margin) {
      return;
    }

    doc.addPage();
    y = margin;
  };

  ensureSpace(14);
  doc.setFillColor(...BRAND.accentDark);
  doc.rect(margin, y, contentWidth, 9, "F");
  doc.setDrawColor(...BRAND.line);
  doc.rect(margin, y, contentWidth, 9, "S");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8.5);
  doc.setTextColor(...BRAND.text);
  doc.text("PRODUKT", columnX[0] + 2, y + 5.8);
  doc.text("PRODUKTNUMMERN", columnX[1] + 2, y + 5.8);
  doc.text("MENGE", columnX[2] + 2, y + 5.8);
  doc.text("EINZELPREIS", columnX[3] + 2, y + 5.8);
  doc.text("SUMME", columnX[4] + 2, y + 5.8);
  y += 9;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(...BRAND.textBlack);

  data.entries.forEach((entry) => {
    const lineTotal = entry.price * entry.quantity;
    const productLines = doc.splitTextToSize(entry.productName, columnWidths[0] - 4);
    const optionLines = doc.splitTextToSize(entry.selectedOptionsSummary || "Standard", columnWidths[0] - 4);
    const articleLines = doc.splitTextToSize(entry.articleNumbersSummary || "-", columnWidths[1] - 4);
    const rowLineCount = Math.max(productLines.length + optionLines.length, articleLines.length, 1);
    const rowHeight = Math.max(12, rowLineCount * lineHeight + 3);

    ensureSpace(rowHeight + 2);

    doc.setDrawColor(...BRAND.line);
    doc.rect(margin, y, contentWidth, rowHeight, "S");
    doc.line(columnX[1], y, columnX[1], y + rowHeight);
    doc.line(columnX[2], y, columnX[2], y + rowHeight);
    doc.line(columnX[3], y, columnX[3], y + rowHeight);
    doc.line(columnX[4], y, columnX[4], y + rowHeight);

    doc.setFont("helvetica", "bold");
    doc.text(productLines, columnX[0] + 2, y + 5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(...BRAND.muted);
    doc.text(optionLines, columnX[0] + 2, y + 5 + productLines.length * lineHeight);
    doc.setTextColor(...BRAND.textBlack);
    doc.text(articleLines, columnX[1] + 2, y + 5);
    doc.text(String(entry.quantity), columnX[2] + columnWidths[2] - 2, y + 5, { align: "right" });
    doc.text(formatPrice(entry.price), columnX[3] + columnWidths[3] - 2, y + 5, { align: "right" });
    doc.setFont("helvetica", "bold");
    doc.text(formatPrice(lineTotal), columnX[4] + columnWidths[4] - 2, y + 5, { align: "right" });
    doc.setFont("helvetica", "normal");

    y += rowHeight;
  });

  const totalQuantity = data.entries.reduce((sum, entry) => sum + entry.quantity, 0);
  const grandTotal = data.entries.reduce((sum, entry) => sum + entry.price * entry.quantity, 0);

  ensureSpace(28);
  const summaryWidth = 68;
  const summaryX = pageWidth - margin - summaryWidth;
  y += 8;
  doc.setFillColor(...BRAND.surface);
  doc.roundedRect(summaryX, y, summaryWidth, 20, 3, 3, "F");
  doc.roundedRect(summaryX, y, summaryWidth, 20, 3, 3, "S");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...BRAND.textBlack);
  doc.text("Gesamtmenge", summaryX + 4, y + 7);
  doc.text(String(totalQuantity), summaryX + summaryWidth - 4, y + 7, { align: "right" });
  doc.line(summaryX + 4, y + 10, summaryX + summaryWidth - 4, y + 10);
  doc.setFont("helvetica", "bold");
  doc.text("Gesamtbetrag", summaryX + 4, y + 17);
  doc.text(formatPrice(grandTotal), summaryX + summaryWidth - 4, y + 17, { align: "right" });

  if (data.paymentIban) {
    ensureSpace(26);
    y += 28;
    doc.setFillColor(...BRAND.surface);
    doc.roundedRect(margin, y, contentWidth, 18, 3, 3, "F");
    doc.roundedRect(margin, y, contentWidth, 18, 3, 3, "S");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.accent);
    doc.text("ZAHLUNGSINFORMATION", margin + 4, y + 6);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...BRAND.textBlack);
    doc.text("Bitte überweise den Rechnungsbetrag auf folgende IBAN:", margin + 4, y + 12);
    doc.setFont("helvetica", "bold");
    doc.text(formatIban(data.paymentIban), margin + 4, y + 16.5);
  }

  if (data.footerHint) {
    ensureSpace(18);
    y += data.paymentIban ? 24 : 28;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...BRAND.muted);
    const hintLines = doc.splitTextToSize(data.footerHint, contentWidth);
    doc.text(hintLines, margin, y);
  }

  doc.save(ensurePdfFileName(data.fileName || data.invoiceNumber));
  return true;
};

